import { success } from '$lib/stores/Toast';
import type { BackOfficeUser } from '$lib/types/BackOfficeUser';
import type { User } from '$lib/types/User';
import { HttpService } from './HttpService';
import type { HttpResponse } from '$lib/types/HttpResponse';
import { SPBError } from './SPBError';
import { translations } from '$lib/translations/i18n';
import { navigate } from '$classes/Router';
import { setSessid } from '$lib/stores/user';

/**
 * Manage Authentification workflow integration with backend and middleware
 */
export class AuthentificationService extends HttpService {
    constructor() {
        super();
    }

    /**
     * Handle Login on the platform
     */
    async login(email: string, password: string, partnerId: string) {
        try {
            const response = (
                await this.post<User>(`${this.endPointUrl}/api/auth/login`, {
                    email: email,
                    password: password,
                    partnerId: partnerId,
                })
            )?.data.response;
            success(translations().LOGIN.SUCCESS);
            if (response?.sessid) {
                setSessid(response.sessid)
            }
            return response;
        } catch (error: any) {
            new SPBError(error.response.data.err.message).show();
        }
    }

    /**
     * Handle Login by token on the platform (used to impersonate an user)
     */
    async loginByToken(token: string, from_be = false) {
        try {
            const response = (
                await this.post<User>(`${this.endPointUrl}/api/auth/token-login`, {
                    token: token,
                    from_be
                })
            )?.data.response;
            success(translations().LOGIN.SUCCESS);
            return response;
        } catch (error: any) {
            new SPBError(error.response.data.err.message).show();
            navigate('/login');
        }
    }

    async register(user: User, type: 'user' | 'business'): Promise<boolean> {
        user.userType = type;
        if (type !== 'business' && !user.isAdult()) {
            throw new SPBError('NOT_ADULT');
        }
        try {
            await this.post(`${this.endPointUrl}/api/auth/signup`, user);
            return true;
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    async forgotPassword(email: string, partnerId: string) {
        try {
            await this.post(`${this.endPointUrl}/api/auth/forgot-password`, {
                email: email,
                partnerId: partnerId,
            });
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    async loginWithGoogle(partnerId: string): Promise<HttpResponse<string>> {
        try {
            return (await this.get<string>(`${this.endPointUrl}/api/auth/google-auth?partnerId=${partnerId}`)).data;
        } catch (error: any) {
            throw new SPBError(error.response.data.err.messaage);
        }
    }

    async resetPassword(token: string, password: string, email: string, partnerId: string) {
        try {
            return await this.post(`${this.endPointUrl}/api/auth/reset-password/${token}`, {
                password: password,
                passwordConfirm: password,
                partnerId: partnerId,
                email: email,
            });
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }
    
    async changePassword(oldPassword: string, newPassword: string, newPasswordConfirm: string, email: string, partnerId: string) {
        try {
            return await this.post(`${this.endPointUrl}/api/auth/change-password`, {
                oldPassword: oldPassword,
                newPassword: newPassword,
                newPasswordConfirm: newPasswordConfirm,
                partnerId: partnerId,
                email: email,
            });
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    async resetBackofficePassword(token: string, password: string, email: string) {
        try {
            return await this.post(`${this.endPointUrl}/api/backoffice/auth/reset-password/`, {
                password: password,
                passwordConfirm: password,
                token: token,
                email: email,
            });
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    async forgotBackofficePassword(email: string) {
        try {
            await this.get(
                `${this.endPointUrl}/api/backoffice/auth/request-new-password?email=${email}`,
                {},
            );
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    async verifyEmail(token: string) {
        try {
            return await this.post(`${this.endPointUrl}/api/auth/verify-email/${token}`);
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    /**
     * Handle logout on the platform
     */
    async logout() {
        await this.post(`${this.endPointUrl}/api/auth/logout`, { body: {} });
    }

    /**
     * Update Profile User info
     * @param user updated user info to be updated
     */
    async update(user: User, contractNumbers: string[]) {
        try {
            await this.post(`${this.endPointUrl}/api/auth/update`, {
                ...user,
                contractNumbers
            });
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    /**
     * Get Email from token
     * @param token token to be used to get the email
     * @returns email
     */
    async getEmailFromToken(token: string): Promise<string> {
        try {
            return (await this.get<string>(`${this.endPointUrl}/api/auth/email-from-token/${token}`))?.data.response.email;
        } catch (error: any) {
            throw new SPBError(error.response.data.err.message);
        }
    }

    /**
     * Get current user in the platform
     * @returns current User data
     */
    async getUser(): Promise<User> {
        return (await this.get<User>(`${this.endPointUrl}/api/auth/current-user`, {}))?.data
            .response;
    }

    /**
     * Get Admin user currently logged in the platform
     * @returns the admin object
     */
    async getAdminUser() {
        try {
            return (
                await this.get<BackOfficeUser>(
                    `${this.endPointUrl}/api/backoffice/auth/current-user`,
                    {},
                )
            )?.data.response;
        } catch (error: any) {}
    }

    /**
     * Login on Backoffice platform
     * @returns
     */
    async loginAdmin(email: string, password: string) {
        try {
            const response = (
                await this.post<BackOfficeUser>(`${this.endPointUrl}/api/backoffice/auth/login`, {
                    email: email,
                    password: password,
                })
            )?.data.response;
            success(translations().LOGIN.SUCCESS);
            return response;
        } catch (error: any) {
            new SPBError(error.response.data.err.message).show();
        }
    }

    async logoutAdmin() {
        await this.post(`${this.endPointUrl}/api/backoffice/auth/logout`, { body: {} });
    }
}
